/**
 * Terms and Conditions Public Page
 * Displays the Terms and Conditions PDF from API
 */

import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Text, Button } from '@app/design-system';
import { colors } from '@app/design-system';
import { policyService, type Policy } from '@app/core';
import { useNavigate } from 'react-router-dom';

interface TermsAndConditionsProps {
  onClose?: () => void;
}

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ onClose }) => {
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadPolicy();
  }, []);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[TermsAndConditions] Loading policy...');
      const latestPolicy = await policyService.getLatestPolicy('TERMS');

      if (!latestPolicy) {
        setError('Terms and Conditions document not available');
        setLoading(false);
        return;
      }

      console.log('[TermsAndConditions] Policy loaded:', latestPolicy);
      setPolicy(latestPolicy);

      // Get the document URL for iframe
      if (latestPolicy.documentAvailable) {
        const url = policyService.getPolicyDocumentUrl(latestPolicy.id);
        console.log('[TermsAndConditions] Document URL:', url);
        setPdfUrl(url);
      }

      setLoading(false);
    } catch (err) {
      console.error('[TermsAndConditions] Failed to load policy:', err);
      setError('Failed to load Terms and Conditions');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/signup');
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Terms_and_Conditions_${policy?.version || 'latest'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
          <Text style={styles.closeButtonText}>✕</Text>
        </TouchableOpacity>

        <View style={styles.headerContent}>
          <Text variant="h1" bold style={styles.title}>
            Terms and Conditions
          </Text>
          <Text variant="body" color="secondary" style={styles.subtitle}>
            Please read these terms carefully before using our service
          </Text>
        </View>

        {!loading && !error && pdfUrl && (
          <TouchableOpacity style={styles.downloadButton} onPress={handleDownload}>
            <Text style={styles.downloadButtonText}>⬇ Download PDF</Text>
          </TouchableOpacity>
        )}
      </View>

      <ScrollView style={styles.contentContainer}>
        <View style={styles.pdfContainer}>
          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={styles.loadingText}>Loading document...</Text>
            </View>
          )}

          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
              <Button
                title="Retry"
                onPress={loadPolicy}
                variant="primary"
                style={styles.retryButton}
              />
            </View>
          )}

          {!loading && !error && pdfUrl && (
            <div style={{ width: '100%', height: '85vh', position: 'relative' }}>
              <embed
                src={pdfUrl}
                type="application/pdf"
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  borderRadius: '8px',
                }}
              />
              {/* Fallback for browsers that don't support embed */}
              <object
                data={pdfUrl}
                type="application/pdf"
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  border: 'none',
                  borderRadius: '8px',
                }}
              >
                <p style={{ textAlign: 'center', padding: '20px' }}>
                  Your browser does not support PDFs.
                  <br />
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
                    Click here to download the PDF
                  </a>
                </p>
              </object>
            </div>
          )}
        </View>

        <View style={styles.footer}>
          <Text variant="body" color="secondary" style={styles.footerText}>
            {policy?.createdAt ? `Last Updated: ${new Date(policy.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}` : 'Last Updated: January 2026'}
          </Text>
          <Text variant="body" color="secondary" style={styles.footerText}>
            © 2026 MyShop. All rights reserved.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    padding: 24,
    paddingTop: 16,
    backgroundColor: colors.primary,
    alignItems: 'center',
    position: 'relative' as any,
  },
  closeButton: {
    position: 'absolute' as any,
    right: 16,
    top: 16,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeButtonText: {
    color: colors.white,
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 24,
  },
  headerContent: {
    alignItems: 'center',
    marginTop: 8,
  },
  downloadButton: {
    marginTop: 16,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: colors.white,
    borderRadius: 8,
  },
  downloadButtonText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  title: {
    color: colors.white,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    color: colors.white,
    textAlign: 'center',
    opacity: 0.9,
  },
  contentContainer: {
    flex: 1,
  },
  pdfContainer: {
    padding: 16,
    minHeight: '85vh',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.gray6,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: 24,
  },
  errorText: {
    fontSize: 16,
    color: colors.error,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    marginTop: 8,
  },
  footer: {
    padding: 24,
    backgroundColor: colors.gray1,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  footerText: {
    marginVertical: 4,
    fontSize: 12,
  },
});

